import { ButtonHTMLAttributes, FC, ReactNode, useMemo } from 'react';
import styles from './styles.module.scss';
import classNames from 'classnames/bind';
import { Icon, TIconType } from '@components/icons';

export type TButtonVariant =
    | 'primary'
    | 'gold'
    | 'red'
    | 'green'
    | 'icon'
    | 'icon-gray'
    | 'icon-dark'
    | 'icon-text'
    | 'outlined-gold'
    | 'outlined-primary'
    | 'outlined-red'
    | 'outlined-green'
    | 'text-gold'
    | 'text-dark'
    | 'text-gray'
    | 'text-white'
    | 'chip'
    | 'active-chip'
    | 'text-red';

export type TButtonSize = 'small' | 'large' | 'medium' | 'custom';

interface IProps {
    variant?: TButtonVariant;
    size?: TButtonSize;
    notify?: boolean;
    notifyCount?: number;
    full?: boolean;
    disabled?: boolean;
    leftIcon?: TIconType;
    rightIcon?: TIconType;
    children?: ReactNode;
    iconSize?: number;
    isLoading?: boolean;
    onClick?: (data?: any) => any;
    className?: string;
}

const cx = classNames.bind(styles);
const Button: FC<Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> & IProps> = ({
    variant,
    size = 'large',
    children,
    className,
    leftIcon,
    rightIcon,
    disabled,
    full,
    notifyCount = 0,
    notify,
    isLoading,
    iconSize,
    ...props
}) => {
    const rootStyles = useMemo(
        () =>
            cx([
                styles.root,
                size,
                variant,
                { notify, disabled, full, isLeftIcon: !!leftIcon, isRightIcon: !!rightIcon },
                className
            ]),
        [size, variant, disabled, full, notify, className, leftIcon, rightIcon]
    );

    return (
        <button className={rootStyles} {...props} disabled={disabled || isLoading}>
            {leftIcon && <Icon name={leftIcon} width={iconSize} height={iconSize} />}
            {isLoading && (
                <div className={styles.spinner_wrapper}>
                    <Icon name="loading_spinner" className={styles.spinner} />
                </div>
            )}
            {children && <div className={styles.children}>{children}</div>}
            {notify && (
                <span className={styles.notification}>
                    <p>{notifyCount > 99 ? `99+` : notifyCount}</p>
                </span>
            )}
            {rightIcon && <Icon name={rightIcon} width={iconSize} height={iconSize} />}
        </button>
    );
};

export default Button;
